/**
 * Tiny skeleton primitive. Compose to match the layout of the content
 * being loaded. The shimmer is delivered via Tailwind's animate-pulse so
 * it stays subtle on a paper background.
 */
export interface SkeletonProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Shape — `text` adds a slight height tweak for prose. */
  shape?: 'text' | 'block' | 'circle'
}

const Skeleton = ({ shape = 'block', className = '', ...rest }: SkeletonProps) => {
  const base = 'animate-pulse bg-mist/40'
  const shapeClass =
    shape === 'circle' ? 'rounded-full' : shape === 'text' ? 'rounded-md h-3' : 'rounded-lg'
  return <div className={`${base} ${shapeClass} ${className}`} {...rest} />
}

/** Multi-line text skeleton — three lines, last one shorter. */
export const SkeletonText = ({ lines = 3, className = '' }: { lines?: number; className?: string }) => (
  <div className={`space-y-2.5 ${className}`}>
    {Array.from({ length: lines }).map((_, i) => (
      <Skeleton
        key={i}
        shape="text"
        className={i === lines - 1 ? 'w-3/5' : 'w-full'}
      />
    ))}
  </div>
)

/** Card-shaped skeleton matching the rounded-3xl bordered card. */
export const SkeletonCard = ({ className = '' }: { className?: string }) => (
  <div className={`rounded-3xl border border-mist/40 bg-white p-6 ${className}`}>
    <Skeleton className="h-4 w-1/3 mb-4" />
    <Skeleton className="h-7 w-2/3 mb-3" />
    <SkeletonText />
  </div>
)

export default Skeleton
